/* eslint-disable @typescript-eslint/naming-convention */
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { BadgeOptions } from 'app/components/badge/badge.interface';
import { DataCollectorService } from 'app/core/services/data_collector_services/data_collector_service.service';
import { LocalisationService } from 'app/core/services/localisation/zuper.localisation.service';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';
import { PickerSelectOptions } from '../data-picker.interface';
import _ from 'lodash';

@Component({
  selector: 'service-data-picker',
  templateUrl: './service-data-picker.component.html',
  styleUrls: ['./service-data-picker.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ServiceDataPickerComponent implements OnInit, OnChanges {
  @Input() filter: any = null;
  @Input() selectOption: PickerSelectOptions = PickerSelectOptions.select;
  @Output() eventEmitter: EventEmitter<any> = new EventEmitter();
  @Input() selectedServices: any[] = [];
  @Input() searchKey: string = '';
  @Input() pickerConfig: any = null;
  @Input() serviceUrl: any = '';
  @Input() pageIndex: number = 0;
  @Input() showSelectedItems: boolean = false;
  @Input() sortBy: string = 'display_order';
  @Input() sortType: string = 'ASC';
  @Input() customParams: any = null;

  services: any[] = [];
  servicesList: any[] = [];
  loaded: boolean = false;
  onSearch: boolean = true;
  defaultParamObj: any = {
    'sort': this.sortType,
    'sort_by': this.sortBy,
    'filter.is_active': true
  };

  constructor(
    private utilityService: UtilityService,
    private dataCollectorService: DataCollectorService,
    private localisationService: LocalisationService) {
  }

  ngOnInit(): void {
    this.localisationService.setDefaultLanguage();
    if (this.selectOption && this.selectOption === PickerSelectOptions.readOnly) {
      delete this.defaultParamObj?.['filter.is_active'];
    }
    this.showDataPickerPanel();
  }

  isSelectedService(service: any): boolean {
    if (this.selectedServices?.length) {
      return this.selectedServices.find(value => value.service_uid === service.service_uid) ? true : false;
    }
    return false;
  }

  selectService(event: any, service: any): void {
    if (this.selectOption === PickerSelectOptions.multiSelect) {
      if (event.checked === true) {
        this.selectedServices.push(service);
      } else {
        this.selectedServices = this.selectedServices.filter(value => value.service_uid !== service.service_uid);
      }
    } else {
      this.selectedServices = [service];
    }
    this.eventEmitter.emit({ value: this.selectedServices });
  }

  triggerSearch(searchKey: any): void {
    delete this.defaultParamObj[this.pickerConfig.params.uniqueSearchKey];
    this.onSearch = true;
    if (this.defaultParamObj[this.pickerConfig.params.searchKey] !== searchKey) {
      const pageIndex: number = 0;
      this.defaultParamObj[this.pickerConfig.params.searchKey] = searchKey;
      this.getData(pageIndex);
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
  }

  sortingAction(key: string, value: string): void {
    if (value) {
      const pageIndex: number = this.pageIndex ?? 0;
      this.defaultParamObj[key] = value;
      this.getData(pageIndex);
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
  }

  getData(pageIndex: number, isOpen?: boolean): void {
    this.loaded = false;
    this.defaultParamObj.page = pageIndex + 1;
    const urlParams = this.utilityService.constructParam(this.defaultParamObj);

    this.dataCollectorService.getData(this.serviceUrl, urlParams).subscribe(
      (response: any) => {
        this.servicesList = response.data;
        this.services = response.data;
        this.loaded = true;
        this.eventEmitter.emit({ totalRecords: response.total_records });
        if (isOpen && this.filter && this.services.length) {
          this.selectedServices = this.selectedServices?.length > 0 ? _.uniqBy([...this.selectedServices, ...this.filter], 'service_uid') : this.filter;
          this.eventEmitter.emit({ value: this.selectedServices });
        } else if (isOpen && this.services.length === 0) {
          this.onSearch = false;
        }
      }, (_error: any) => {
        this.loaded = true;
      });
  }

  showDataPickerContent(): void {
    const pageIndex: number = this.pageIndex ?? 0;
    Object.keys(this.pickerConfig.params).forEach((key: any) => {
      const paramKey: any = this.pickerConfig.params[key];
      if (key !== 'uniqueSearchKey') {
        let value: any = '';
        if (key === 'countKey') {
          value = 10;
        } else if (key === 'pageKey') {
          value = 1;
        } else if (key === 'searchKey' && !this.showSelectedItems && this.searchKey) {
          value = this.searchKey;
        }
        this.defaultParamObj[paramKey] = value;
      }
    });
    if (this.customParams && Object.keys(this.customParams).length) {
      this.defaultParamObj = { ...this.defaultParamObj, ...this.customParams };
    }
    if (this.filter) {
      this.updateFilterValue();
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
    this.eventEmitter.emit({ sortBy: this.defaultParamObj['sort_by'], sortType: this.defaultParamObj['sort'] });
    this.getData(pageIndex, true);
  }

  updateFilterValue(): void {
    if (this.selectOption === PickerSelectOptions.select && this.filter.length > 0) {
      if (this.filter[0].service_name) {
        this.defaultParamObj[this.pickerConfig.params.searchKey] = this.filter[0].service_name;
        this.eventEmitter.emit({ searchKey: this.filter[0].service_name });
      } else {
        this.defaultParamObj[this.pickerConfig.params.uniqueSearchKey] = this.filter[0].service_uid;
      }
    }
  }

  resetServiceDataPicker(): void {
    this.services = [];
  }

  showDataPickerPanel(): void {
    this.resetServiceDataPicker();
    if (this.showSelectedItems) {
      this.services = this.selectedServices;
    } else if (this.servicesList.length === 0 && this.searchKey) {
      this.showDataPickerContent();
    } else if (this.servicesList.length > 0 || this.searchKey) {
      this.services = this.servicesList;
    } else {
      this.showDataPickerContent();
    }
  }

  hideSelectedItemsPanel(): void {
    this.eventEmitter.emit({ showSelectedItems: false, value: this.selectedServices, filter: this.selectedServices });
  }

  getBadgeOptions(): BadgeOptions {
    return {
      type: 'PRIMARY',
      displayKey: null,
      displayName: this.selectedServices ? this.selectedServices.length : 0,
      displayType: 'listingCount'
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.searchKey?.firstChange === false) {
      this.triggerSearch(changes?.searchKey?.currentValue);
    }

    if (changes?.pageIndex?.previousValue !== undefined) {
      this.getData(changes?.pageIndex?.currentValue);
    }

    if (changes?.showSelectedItems?.firstChange === false) {
      this.showDataPickerPanel();
    }

    if (changes?.sortBy?.firstChange === false && changes?.sortBy?.previousValue !== undefined) {
      this.sortingAction('sort_by', changes?.sortBy?.currentValue);
    }

    if (changes?.sortType?.firstChange === false && changes?.sortType?.previousValue !== undefined) {
      this.sortingAction('sort', changes?.sortType?.currentValue);
    }
  }

  isValidValue(value: any): boolean {
    return value !== undefined && value !== null;
  }
}
